import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
	SmartCard,
	Spinner,
	SpinnerDialog,
	LocationMethod,
} from '@silverrailtech/srtlibrary';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import messages from '../../../messagesCustomerAccounts';
import {
	asyncApiCall,
	deleteCustomerAccountData,
	getCustomerAccountData,
	getSmartCardPrintingLocations,
	patchCustomerAccountData,
	postCustomerAccountData,
	submitPrintSmartcard,
} from '../../../../api';
import { getStationDataByCodeTypeApi } from '../../../../components/apiComponents';
import { LOCAL_STORAGE_KEYS } from '../../../../constants/constants';
import ExtendedSnackbar from '../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import { SmartCardWrapper } from '../../ManageCustomerAccountStyled';
import { buildSmartCardPayload, formatPrice } from '../../../../utils';
import useAuth from '../../../../hooks/useAuth';
import useRequestor from '../../../../hooks/useRequestor';

const resolveStationNames = async (codes) => {
	const uniqueCodes = [...new Set(codes.filter(Boolean))];

	if (uniqueCodes.length === 0) return {};

	const results = await Promise.all(
		uniqueCodes.map(async (code) => {
			try {
				const isNlcCode = /^\d+$/.test(code);
				const response = await asyncApiCall(
					getStationDataByCodeTypeApi,
					code,
					isNlcCode ? 'NLC' : null,
				);
				const stationName =
					response?.successResponse?.data?.StationAttrs?.StationAttrGeneralInfo
						?.StationAttrGeneralInfoStationName ?? code;
				return [code, stationName];
			} catch {
				return [code, code];
			}
		}),
	);

	return Object.fromEntries(results);
};

const getDisplayCardNumber = (card) =>
	card?.isrnNumber || card?.reference || '';

const STATIONS = [
	{ name: 'Card bureau', value: LocationMethod.CARD_BUREAU_OFFICE },
	{ name: 'Local Printer', value: LocationMethod.LOCAL_PRINTER },
];

const SmartCards = ({ addresses }) => {
	const intl = useIntl();
	const { getAuthHeaders, accountId } = useAuth();
	const { apiCallWithAuthRetry } = useRequestor();

	const { isLocalPrinterEnabled } = useFlags();

	const [formData, setFormData] = useState([]);
	const [confirmationModalConfig, setConfirmationModalConfig] = useState({
		isOpen: false,
		type: null,
	});

	const [isPending, setIsPending] = useState(true);
	const [isReplacePending, setIsReplacePending] = useState(false);
	const [isApplyingSmartCard, setIsApplyingSmartCard] = useState(false);
	const [error, setError] = useState('');
	const [applyError, setApplyError] = useState('');
	const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
	const [replaceSmartCardStatus, setReplaceSmartCardStatus] = useState(null);

	const [products, setProducts] = useState([]);
	const [printers, setPrinters] = useState([]);

	const stations = useMemo(() => {
		if (isLocalPrinterEnabled) {
			return STATIONS;
		}

		return STATIONS.filter(
			(station) => station.value !== LocationMethod.LOCAL_PRINTER,
		);
	}, [isLocalPrinterEnabled]);

	const defaultPrinterLocation = isLocalPrinterEnabled
		? LocationMethod.LOCAL_PRINTER
		: LocationMethod.CARD_BUREAU_OFFICE;

	const getDefaultPrinter = useCallback(() => {
		try {
			const savedPrinter = localStorage.getItem(
				LOCAL_STORAGE_KEYS.defaultSmartcardPrinter,
			);
			return savedPrinter ? JSON.parse(savedPrinter) : null;
		} catch {
			return null;
		}
	}, []);

	const handleDefaultPrinterChange = useCallback((printer, saveAsDefault) => {
		if (saveAsDefault && printer) {
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.defaultSmartcardPrinter,
				JSON.stringify(printer),
			);
		} else if (!saveAsDefault) {
			localStorage.removeItem(LOCAL_STORAGE_KEYS.defaultSmartcardPrinter);
		}
	}, []);

	const fetchSmartCards = useCallback(() => {
		setIsPending(true);
		if (accountId) {
			apiCallWithAuthRetry({
				apiCall: (onError, onSuccess, refreshedHeaders) => {
					getCustomerAccountData(
						{ fetchRenewals: true },
						refreshedHeaders || getAuthHeaders(),
						`/${accountId}/smartcards`,
						onError,
						onSuccess,
					);
				},
				onError: (err) => {
					setIsPending(false);
					setError(err.message || 'Failed to load Smartcards');
				},
				onSuccess: async (data) => {
					const allCodes = data
						.flatMap((card) => card.renewals || [])
						.flatMap((renewal) => [renewal.origin, renewal.destination]);

					const stationNameMap = await resolveStationNames(allCodes);

					const enrichedData = data.map((card) => ({
						...card,
						isrnNumber: getDisplayCardNumber(card),
						renewals: (card.renewals || []).map((renewal) => ({
							...renewal,
							origin: stationNameMap[renewal.origin] || renewal.origin,
							destination:
								stationNameMap[renewal.destination] || renewal.destination,
						})),
					}));

					setFormData(enrichedData);
					setIsPending(false);
					setError('');
				},
			});
		}
	}, [accountId, getAuthHeaders, apiCallWithAuthRetry]);

	useEffect(() => {
		fetchSmartCards();
	}, [accountId, getAuthHeaders]);

	useEffect(() => {
		const headers = getAuthHeaders();
		if (headers) {
			apiCallWithAuthRetry({
				apiCall: (onError, onSuccess, refreshedHeaders) => {
					getSmartCardPrintingLocations(
						{ headers: refreshedHeaders || headers },
						onError,
						onSuccess,
					);
				},
				onError: (err) => {
					setError(err.message || 'Failed to load printing locations');
				},
				onSuccess: (data) => {
					setPrinters(
						data?.map((printer) => ({
							name: printer.location,
							value: printer.location,
						})) || [],
					);
				},
			});
		}
	}, [getAuthHeaders, apiCallWithAuthRetry]);

	const onNewSmartCardSubmit = (smartCard) => {
		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				patchCustomerAccountData(
					{ isrn: smartCard.isrnNumber, nickname: smartCard.nickname },
					onError,
					onSuccess,
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/smartcards`,
				);
			},
			onError: (err) => {
				const errorMessage = err.errorResponse?.message || err.message;
				setError(errorMessage || 'Failed to set Smartcard');
				setIsPending(false);
			},
			onSuccess: () => {
				fetchSmartCards();
			},
		});
	};

	const onHotlistSmartCard = (smartCard) => {
		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				patchCustomerAccountData(
					null,
					onError,
					onSuccess,
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/smartcards/${smartCard.isrnNumber}/hotlisting`,
				);
			},
			onError: (err) => {
				const errorMessage = err.errorResponse?.message || err.message;
				setError(errorMessage || 'Failed to hotlist Smartcard');
				setIsPending(false);
			},
			onSuccess: () => {
				setError('');
				fetchSmartCards();
			},
		});
	};

	const onEditSmartCardSubmit = (smartCard) => {
		const newList = formData.map((card) =>
			card.id === smartCard.id ? smartCard : card,
		);
		setFormData(newList);
	};

	const onDeleteSmartCardSubmit = (smartCard) => {
		const smartCardReference = smartCard.reference;
		setIsPending(true);
		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				deleteCustomerAccountData(
					null,
					onError,
					onSuccess,
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/smartcards/${smartCardReference}`,
				);
			},
			onError: (err) => {
				const errorMessage = err.errorResponse?.message || err.message;
				setError(errorMessage || 'Failed to delete Smartcard');
				setIsPending(false);
			},
			onSuccess: () => {
				fetchSmartCards();
			},
		});
	};

	const onApplyCardSubmit = (smartCard) => {
		setIsApplyingSmartCard(true);
		setIsPending(true);
		const payload = buildSmartCardPayload(smartCard);
		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				postCustomerAccountData(
					payload,
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/smartcards`,
					onError,
					onSuccess,
				);
			},
			onError: (err) => {
				const errorMessage = err.errorResponse?.message || err.message;
				setApplyError(errorMessage || 'Failed to set Smartcard');
				setIsPending(false);
				setIsApplyingSmartCard(false);
			},
			onSuccess: () => {
				setConfirmationModalConfig({
					isOpen: true,
					type: 'apply-smartcard-ok',
				});
				setIsApplyingSmartCard(false);
				fetchSmartCards();
			},
		});
	};

	const onSeasonRenewalUpdate = (renewalId, updates) => {
		setIsPending(true);

		const renewal = formData
			.flatMap((card) => card.renewals || [])
			.find((r) => r.id === renewalId);

		if (!renewal) {
			setError('Season renewal not found');
			setIsPending(false);
			return;
		}

		const patchBody = {};

		if (updates.status) {
			patchBody.status = updates.status;
		}

		if (updates.counter !== undefined) {
			patchBody.counter = updates.counter;
		}

		if (updates.renewalDate) {
			patchBody.renewalDate = updates.renewalDate;
		}

		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				patchCustomerAccountData(
					patchBody,
					onError,
					onSuccess,
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/renewals/${renewalId}`,
				);
			},
			onError: (err) => {
				const errorMessage = err.errorResponse?.message || err.message;
				setError(errorMessage || 'Failed to update season renewal');
				setIsPending(false);
			},
			onSuccess: () => {
				setError('');
				fetchSmartCards();
			},
		});
	};

	const onAgentActionSubmit = (type, smartcard, seasonRenewal) => {
		if (type === 'delete') {
			onDeleteSmartCardSubmit(smartcard);
		}

		if (type === 'hotlist') {
			onHotlistSmartCard(smartcard);
		}

		if (type === 'cancel-continuous-authority' && seasonRenewal) {
			onSeasonRenewalUpdate(seasonRenewal.id, { status: 'CANCELLED' });
		}

		setConfirmationModalConfig({
			isOpen: false,
			type: null,
		});
	};

	const toggleIsReplaceModalOpen = useCallback(() => {
		setIsReplaceModalOpen((prevIsReplaceModalOpen) => !prevIsReplaceModalOpen);
	}, []);

	const fetchProducts = useCallback(
		(smartCard) => {
			setReplaceSmartCardStatus(null);

			if (!smartCard) {
				return;
			}

			setIsReplacePending(true);

			apiCallWithAuthRetry({
				apiCall: (onError, onSuccess, refreshedHeaders) => {
					getCustomerAccountData(
						null,
						refreshedHeaders || getAuthHeaders(),
						`/${accountId}/smartcards/${smartCard.isrnNumber}/products?fields=include:replaceable`,
						onError,
						onSuccess,
					);
				},
				onError: (productsError) => {
					setProducts([]);
					setError(productsError.message || 'Failed to load products');
					toggleIsReplaceModalOpen();
					setIsReplacePending(false);
				},
				onSuccess: async (response) => {
					const allCodes = response.flatMap((product) => [
						product.origin,
						product.destination,
					]);

					const stationNameMap = await resolveStationNames(allCodes);

					const nextProducts = response.map((product) => ({
						...product,
						originStationName: stationNameMap[product.origin] || product.origin,
						destinationStationName:
							stationNameMap[product.destination] || product.destination,
						price: {
							...product.price,
							amount: formatPrice(
								product?.price?.amount,
								product?.price?.currency,
								intl,
								{ includeCurrency: false },
							),
						},
					}));

					setProducts(nextProducts);
					toggleIsReplaceModalOpen();
					setIsReplacePending(false);
				},
			});
		},
		[
			accountId,
			getAuthHeaders,
			intl,
			toggleIsReplaceModalOpen,
			apiCallWithAuthRetry,
		],
	);

	const handlePrinterConfirm = useCallback(
		(formDataApply) => {
			const payload = buildSmartCardPayload(formDataApply, accountId);

			apiCallWithAuthRetry({
				apiCall: (onError, onSuccess, refreshedHeaders) => {
					submitPrintSmartcard(
						payload,
						refreshedHeaders || getAuthHeaders(),
						onError,
						onSuccess,
					);
				},
				onError: (err) => {
					setError(err.errorResponse.message || 'Failed to print smartcard');
				},
				onSuccess: () => {
					fetchSmartCards();
				},
			});
		},
		[accountId, getAuthHeaders, fetchSmartCards, apiCallWithAuthRetry],
	);

	const handleSmartCardReplaceSumbit = useCallback(
		(smartCard, smartCardFormData) => {
			if (!smartCard || !smartCardFormData) {
				return;
			}

			const addressData = smartCardFormData?.addresses ?? {};

			const addressesList = {
				postcode: addressData.postCode,
				address1: addressData.addressLineOne,
				address2: addressData.addressLineTwo,
				address3: addressData.addressLineThree,
				address4: addressData.town,
				address5: addressData.countryCode,
				address6: addressData.county,
			};

			setIsReplacePending(true);

			const payload = {
				nickname: smartCard.nickname,
				title: smartCard.title,
				firstName: smartCard.firstName,
				lastName: smartCard.lastName,
				dateOfBirth: smartCard.dateOfBirth,
				location: 'CBO',
				addresses: addressesList,
				addAddressToAccount: smartCardFormData.addAddressToAccount,
				oldIsrn: smartCard.isrnNumber,
			};

			apiCallWithAuthRetry({
				apiCall: (onError, onSuccess, refreshedHeaders) => {
					postCustomerAccountData(
						payload,
						refreshedHeaders || getAuthHeaders(),
						`/${accountId}/smartcards/replace`,
						onError,
						onSuccess,
					);
				},
				onError: (productsError) => {
					setReplaceSmartCardStatus('error');
					setError(productsError.message || 'Failed to replace smartcard');
					fetchSmartCards();
					setIsReplacePending(false);
				},
				onSuccess: () => {
					toggleIsReplaceModalOpen();
					setReplaceSmartCardStatus('success');
					fetchSmartCards();
					setIsReplacePending(false);
				},
			});
		},
		[
			accountId,
			getAuthHeaders,
			fetchSmartCards,
			toggleIsReplaceModalOpen,
			apiCallWithAuthRetry,
		],
	);

	const replaceModalConfig = useMemo(
		() => ({
			isOpen: isReplaceModalOpen,
			type: 'products-list',
		}),
		[isReplaceModalOpen],
	);

	const props = {
		localPrinters: printers || [],
		defaultPrinter: getDefaultPrinter(),
		defaultPrinterLocation,
		onDefaultPrinterChange: handleDefaultPrinterChange,
		formData,
		showApplyForm: true,
		showEditButton: false,
		noItemsViewVariant: 'warning',
		onNewSmartCardSubmit,
		handlePrinterConfirm,
		onEditSmartCardSubmit,
		onDeleteSmartCardSubmit,
		isAgent: true,
		applyError,
		setError: setApplyError,
		savedAddresses: addresses,
		confirmationModalConfig,
		setConfirmationModalConfig,
		onApplyCardSubmit,
		onAgentActionSubmit,
		onSeasonRenewalUpdate,
		stations,
		onReplaceClick: fetchProducts,
		onReplaceProductsSubmit: handleSmartCardReplaceSumbit,
		onReplaceProductsCancel: toggleIsReplaceModalOpen,
		products,
		replaceModalConfig,
		replaceSmartCardConfirmStatus: replaceSmartCardStatus,
	};

	return (
		<div className="container">
			<div className="row">
				<SmartCardWrapper className="col-12">
					<SmartCard
						{...props}
						title={intl.formatMessage(messages.lblSmartCardsTitle)}
					/>
					<ExtendedSnackbar
						id="srtSmartcardSnackBar"
						open={error !== ''}
						message={error}
						onClose={() => setError('')}
					/>
					{isPending && <Spinner />}
				</SmartCardWrapper>
			</div>
			<SpinnerDialog open={isReplacePending || isApplyingSmartCard} />
		</div>
	);
};

SmartCards.propTypes = {
	addresses: PropTypes.array,
};

export default SmartCards;
